import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, Repeat, Check } from "lucide-react";
import { toast } from "sonner";
import { fetchRefill, fetchEndorsements, formatUsd, createSubscriptionCheckout } from "../lib/api";
import { useCart } from "../context/CartContext";
import { REFILLS_IDS } from "../constants/testIds";

const INTERVALS = [
  { days: 30, label: "Every 30 days" },
  { days: 60, label: "Every 60 days" },
  { days: 90, label: "Every 90 days" },
];

export default function RefillDetailPage() {
  const { id } = useParams();
  const [refill, setRefill] = useState(null);
  const [endorsement, setEndorsement] = useState(null);
  const [qty, setQty] = useState(1);
  const [mode, setMode] = useState("once");
  const [interval, setIntervalDays] = useState(30);
  const [subEmail, setSubEmail] = useState("");
  const [subLoading, setSubLoading] = useState(false);
  const { addRefill } = useCart();

  useEffect(() => { fetchRefill(id).then(setRefill); }, [id]);
  useEffect(() => {
    if (!refill) return;
    fetchEndorsements().then((all) => setEndorsement(all[refill.brand] || null));
  }, [refill]);

  if (!refill) return <div className="max-w-7xl mx-auto px-8 py-24 font-serif text-2xl">Loading…</div>;

  const subPrice = Math.round(refill.price * 0.9);

  const subscribe = async () => {
    if (!subEmail) { toast.error("Add your email so we can send the subscription confirmation."); return; }
    setSubLoading(true);
    try {
      const res = await createSubscriptionCheckout({
        refill_id: refill.id, interval_days: interval,
        origin_url: window.location.origin, email: subEmail,
      });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error("Could not start subscription.");
      setSubLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-16 py-12 lg:py-20">
      <Link to="/refills" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[#6B5757] hover:text-[#2A1F1F]">
        <ArrowLeft className="w-3 h-3" /> All Refills
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 mt-8">
        <div className="aspect-[4/5] overflow-hidden bg-[#FDF3ED] rounded-xl">
          <img src={refill.image} alt={refill.name} className="w-full h-full object-cover" />
        </div>

        <div>
          <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#B85C56]">{refill.brand}</div>
          <h1 className="font-serif text-4xl sm:text-5xl mt-3 leading-[1.05] tracking-tight text-[#2A1F1F]">{refill.name}</h1>
          <div className="text-[10px] uppercase tracking-widest text-[#6B5757] mt-3">{refill.category}</div>
          <p className="text-[#6B5757] mt-8 leading-relaxed">{refill.desc}</p>

          {endorsement && (
            <div className="mt-10 bg-[#FDF3ED] border-l-2 border-[#B85C56] p-6 rounded-lg">
              <div className="font-serif text-2xl text-[#B85C56] leading-none">"</div>
              <p className="font-serif text-lg text-[#2A1F1F] leading-snug mt-2 italic">{endorsement.quote}</p>
              <div className="mt-4 border-t border-[#F0DFD0] pt-3">
                <div className="font-medium text-sm text-[#2A1F1F]">{endorsement.author}</div>
                <div className="text-xs text-[#6B5757] mt-1">{endorsement.credentials}</div>
              </div>
            </div>
          )}

          <div className="mt-10 grid grid-cols-2 gap-3">
            <button data-testid="refill-mode-once" onClick={() => setMode("once")}
              className={`px-4 py-4 border rounded-lg text-left transition-colors ${
                mode === "once" ? "border-[#2A1F1F] bg-white" : "border-[#E5CDBB] bg-white/70 hover:border-[#2A1F1F]"
              }`}>
              <div className="flex items-center gap-2">
                {mode === "once" && <Check className="w-4 h-4 text-[#B85C56]" />}
                <div className="font-medium text-sm text-[#2A1F1F]">Buy Once</div>
              </div>
              <div className="font-serif text-2xl mt-1">{formatUsd(refill.price)}</div>
            </button>
            <button data-testid="refill-mode-subscribe" onClick={() => setMode("subscribe")}
              className={`px-4 py-4 border rounded-lg text-left transition-colors relative ${
                mode === "subscribe" ? "border-[#B85C56] bg-[#FDF3ED]" : "border-[#E5CDBB] bg-white/70 hover:border-[#B85C56]"
              }`}>
              <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#B85C56] text-white text-[9px] font-mono uppercase tracking-widest">Save 10%</div>
              <div className="flex items-center gap-2">
                <Repeat className="w-4 h-4 text-[#B85C56]" />
                <div className="font-medium text-sm text-[#2A1F1F]">Subscribe</div>
              </div>
              <div className="font-serif text-2xl mt-1">{formatUsd(subPrice)}</div>
              <div className="text-[10px] text-[#6B5757] mt-1 font-mono uppercase tracking-widest">Cancel any time</div>
            </button>
          </div>

          {mode === "subscribe" && (
            <div data-testid="refill-subscription-panel" className="mt-6 p-5 border border-[#F0DFD0] rounded-lg bg-white">
              <div className="font-mono text-[10px] tracking-widest uppercase text-[#B85C56] mb-3">Frequency</div>
              <div className="flex flex-wrap gap-2">
                {INTERVALS.map((i) => (
                  <button key={i.days} data-testid={`refill-interval-${i.days}`}
                    onClick={() => setIntervalDays(i.days)}
                    className={`px-3 py-2 text-xs border rounded-full transition-colors ${
                      interval === i.days ? "bg-[#2A1F1F] text-[#FBF6F1] border-[#2A1F1F]" : "bg-white text-[#2A1F1F] border-[#E5CDBB] hover:border-[#2A1F1F]"
                    }`}>
                    {interval === i.days && <Check className="w-3 h-3 inline mr-1" />}
                    {i.label}
                  </button>
                ))}
              </div>
              <input data-testid="refill-sub-email" type="email" placeholder="Email for the subscription"
                value={subEmail} onChange={(e) => setSubEmail(e.target.value)}
                className="mt-4 w-full px-4 py-3 text-sm bg-white border border-[#E5CDBB] rounded-md focus:outline-none focus:border-[#2A1F1F]" />
              <button data-testid="refill-subscribe-submit" onClick={subscribe} disabled={subLoading}
                className="btn-primary w-full mt-4 disabled:opacity-60 inline-flex items-center justify-center gap-3">
                {subLoading ? "Redirecting…" : `Start Subscription · ${formatUsd(subPrice)} every ${interval} days`}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          {mode === "once" && (
            <div className="mt-6 flex items-center gap-4">
              <div className="flex items-center border border-[#E5CDBB] rounded-full">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-3">−</button>
                <span className="px-4 font-mono text-sm">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-3 py-3">+</button>
              </div>
              <button data-testid={REFILLS_IDS.detailAddToCart}
                onClick={() => { addRefill(refill, qty); toast.success(`${qty} × ${refill.name} added`); }}
                className="btn-primary flex-1 inline-flex items-center justify-center gap-3">
                Add {qty} to Cart · {formatUsd(refill.price * qty)} <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

          <div className="mt-8 grid grid-cols-3 gap-2 text-[10px] font-mono tracking-widest uppercase text-[#6B5757]">
            <div className="border border-[#F0DFD0] p-3 text-center rounded-md">Surgeon Approved</div>
            <div className="border border-[#F0DFD0] p-3 text-center rounded-md">Medical Grade</div>
            <div className="border border-[#F0DFD0] p-3 text-center rounded-md">Authentic Brand</div>
          </div>
        </div>
      </div>
    </div>
  );
}
